import { useState, useCallback, useEffect, useRef } from "react";
import { getIndicadores } from "modules/indicadores/services/indicadoresService";
import { createIndicadoresClient } from "../model/indicadoresClient";
import { EMPTY_FILTERS, DEFAULT_QUERY, validateFilters } from "../model/indicadoresModel";

export const useIndicadores = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ phase: "download", loaded: 0, total: null });
  const [tableLoading, setTableLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [exportError, setExportError] = useState("");
  const lastExport = useRef("pdf");
  const [filtros, setFiltros] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const client = useRef(null);
  const revision = useRef(0);
  const tableRevision = useRef(0);
  const request = useRef(null);
  const currentFilters = useRef(EMPTY_FILTERS);
  const currentQuery = useRef(DEFAULT_QUERY);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    const id = ++revision.current;
    ++tableRevision.current;
    request.current?.abort();
    request.current = new AbortController();
    setExportError("");
    setLoading(true);
    setProgress({ phase: "download", loaded: 0, total: null });
    setError("");
    try {
      const response = await getIndicadores({
        signal: request.current.signal,
        onDownloadProgress: ({ loaded, total }) => {
          if (mounted.current && revision.current === id)
            setProgress({
              phase: "download",
              loaded: loaded || 0,
              total: total > 0 ? total : null,
            });
        },
      });
      if (!mounted.current || revision.current !== id) return;
      setProgress({ phase: "process", loaded: 0, total: null });
      client.current?.dispose();
      client.current = createIndicadoresClient();
      const result = await client.current.request("load", {
        response,
        filters: currentFilters.current,
        query: { ...currentQuery.current, page: 0 },
      });
      if (!mounted.current || revision.current !== id) return;
      currentQuery.current = { ...currentQuery.current, page: result.page.page };
      setQuery(currentQuery.current);
      setData(result);
    } catch (err) {
      if (mounted.current && revision.current === id)
        setError(err.message || "Error al cargar indicadores.");
    } finally {
      if (mounted.current && revision.current === id) {
        setLoading(false);
        setTableLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
      ++revision.current;
      ++tableRevision.current;
      request.current?.abort();
      client.current?.dispose();
    };
  }, [refresh]);

  const cancelLoad = useCallback(() => {
    ++revision.current;
    ++tableRevision.current;
    request.current?.abort();
    setLoading(false);
    setTableLoading(false);
    setError("Consulta cancelada. Puedes reintentar cuando lo necesites.");
  }, []);

  const handleFilter = useCallback(
    async (filters) => {
      const invalid = validateFilters(filters);
      if (invalid) {
        setError(invalid);
        return;
      }
      if (!data || loading || exporting) return;
      const id = ++revision.current;
      ++tableRevision.current;
      setExportError("");
      setLoading(true);
      setProgress({ phase: "filter", loaded: 0, total: null });
      setError("");
      const nextQuery = { ...currentQuery.current, page: 0 };
      try {
        const result = await client.current.request("filter", { filters, query: nextQuery });
        if (!mounted.current || revision.current !== id) return;
        currentFilters.current = filters;
        currentQuery.current = nextQuery;
        setFiltros(filters);
        setQuery(nextQuery);
        setData(result);
      } catch (err) {
        if (mounted.current && revision.current === id) setError(err.message);
      } finally {
        if (mounted.current && revision.current === id) {
          setLoading(false);
          setTableLoading(false);
        }
      }
    },
    [data, loading, exporting]
  );

  const handleQuery = useCallback(
    async (nextQuery) => {
      if (loading || !data || exporting) return;
      const id = ++tableRevision.current;
      const dataRevision = revision.current;
      currentQuery.current = nextQuery;
      setQuery(nextQuery);
      setTableLoading(true);
      try {
        const page = await client.current.request("query", nextQuery);
        if (!mounted.current || id !== tableRevision.current || dataRevision !== revision.current)
          return;
        currentQuery.current = { ...nextQuery, page: page.page };
        setQuery(currentQuery.current);
        setData((previous) => ({ ...previous, page }));
      } catch (err) {
        if (mounted.current && id === tableRevision.current && dataRevision === revision.current)
          setError(err.message);
      } finally {
        if (mounted.current && id === tableRevision.current) setTableLoading(false);
      }
    },
    [data, loading, exporting]
  );

  const exportFile = useCallback(
    async (format) => {
      if (loading || tableLoading || exporting || !data) return;
      setExporting(true);
      setExportError("");
      lastExport.current = format;
      try {
        const result =
          format === "pdf"
            ? await (
                await import("../model/indicadoresPdf")
              ).createIndicadoresPdf({
                summary: data.summary,
                filters: currentFilters.current,
                totalLoaded: data.totalLoaded,
              })
            : await client.current.request("export", { format });
        if (!mounted.current) return;
        const url = URL.createObjectURL(new Blob([result.content], { type: result.type }));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `indicadores-${
          format === "xlsx-detail" ? "tickets-detallados" : format === "xlsx" ? "resumen" : format
        }-${currentFilters.current.fechaInicio || "inicio"}-${
          currentFilters.current.fechaFin || "actual"
        }.${result.extension}`;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        if (mounted.current) setExportError(err.message || "No se pudo generar el reporte.");
      } finally {
        if (mounted.current) setExporting(false);
      }
    },
    [loading, tableLoading, exporting, data]
  );

  return {
    data,
    loading,
    progress,
    cancelLoad,
    tableLoading,
    exporting,
    exportError,
    retryExport: () => exportFile(lastExport.current),
    error,
    filtros,
    query,
    handleFilter,
    handleQuery,
    refresh,
    exportFile,
  };
};
